import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, Download, RotateCcw } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PaginationControls } from "@/components/common/PaginationControl";
import {
  CouponApi,
  TransactionReport,
  GetTransactionReportRequest,
} from "@/api/giftcard.api";
import { useToast } from "@/hooks/use-toast";
import { HandleError } from "@/api/axiosClient";
import { CouponTableSkeleton } from "@/components/ui/coupon-table-skeleton";
import { Input } from "@/components/ui/input";
import { formatReportAmount, formatTxnDateTime, displayReportText, mapTransactionReportList } from "./utils";

interface CouponsTransactionReportProps {
  onLoad?: () => void;
}

export function CouponsTransactionReport({
  onLoad,
}: CouponsTransactionReportProps) {
  const [transactionReports, setTransactionReports] = useState<
    TransactionReport[]
  >([]);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [filterMobileNo, setFilterMobileNo] = useState<string>("");
  const [mobileNoInput, setMobileNoInput] = useState<string>("");
  const [filterFromDate, setFilterFromDate] = useState<Date | undefined>();
  const [filterToDate, setFilterToDate] = useState<Date | undefined>();
  const [filterTxnType, setFilterTxnType] = useState<string>("");
  const [filterFromDateOpen, setFilterFromDateOpen] = useState(false);
  const [filterToDateOpen, setFilterToDateOpen] = useState(false);
  const [transactionPage, setTransactionPage] = useState<number>(0);
  const [transactionPageSize, setTransactionPageSize] = useState<number>(10);
  const [transactionTotalRecords, setTransactionTotalRecords] =
    useState<number>(0);
  const { toast } = useToast();

  // Helper function to format date as YYYY-MM-DD without timezone conversion
  const formatDateForAPI = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}T00:00:00`;
  };

  const fetchTransactionReports = async () => {
    setIsLoadingReports(true);
    try {
      const payload: GetTransactionReportRequest = {
        p_mobileNo: filterMobileNo || null,
        p_fromDate: filterFromDate ? formatDateForAPI(filterFromDate) : null,
        p_toDate: filterToDate ? formatDateForAPI(filterToDate) : null,
        p_txnType:
          filterTxnType === "NoEarnRedeem"
            ? "-"
            : filterTxnType || null,
        p_PageNumber: transactionPage + 1,
        p_PageSize: transactionPageSize,
      };
      const response = await CouponApi.getTransactionReport(payload);
      const mappedReports = mapTransactionReportList(
        response.transactionReportList
      );
      setTransactionReports(mappedReports);
      setTransactionTotalRecords(response.total_records ?? mappedReports.length);

      if (response.error_msg?.trim()) {
        toast({
          title: "Warning",
          description: response.error_msg,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching transaction reports:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch transaction reports",
        variant: "destructive",
      });
      setTransactionReports([]);
      setTransactionTotalRecords(0);
    } finally {
      setIsLoadingReports(false);
      if (onLoad) onLoad();
    }
  };

  // Debounce mobile number input
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilterMobileNo(mobileNoInput);
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timer);
  }, [mobileNoInput]);

  // Reset page to 0 when filters change
  useEffect(() => {
    setTransactionPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterMobileNo, filterFromDate, filterToDate, filterTxnType]);

  useEffect(() => {
    fetchTransactionReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterMobileNo,
    filterFromDate,
    filterToDate,
    filterTxnType,
    transactionPage,
    transactionPageSize,
  ]);

  // Reset all filters
  const handleResetFilters = () => {
    setMobileNoInput("");
    setFilterMobileNo("");
    setFilterFromDate(undefined);
    setFilterToDate(undefined);
    setFilterTxnType("");
    setFilterFromDateOpen(false);
    setFilterToDateOpen(false);
    setTransactionPage(0);
  };

  // Export transactions to CSV
  const handleExportTransactions = () => {
    if (transactionReports.length === 0) {
      toast({
        title: "No Data",
        description: "No transaction data available to export",
        variant: "destructive",
      });
      return;
    }

    try {
      // Define CSV headers
      const headers = [
        "Date",
        "Mobile Number",
        "Invoice Number",
        "Invoice Amount",
        "Transaction Type",
        "Coupon Code",
        "Redeem Amount",
      ];

      // Convert data to CSV rows
      const csvRows = [
        headers.join(","),
        ...transactionReports.map((row) => {
          return [
            formatTxnDateTime(row.txnDate),
            row.mobileNo || "",
            row.invoiceNo || "",
            formatReportAmount(row.invoiceAmount),
            displayReportText(row.transactionType),
            displayReportText(row.couponCode),
            formatReportAmount(row.redeemAmount),
          ]
            .map((field) => {
              // Escape commas and quotes in CSV
              const stringField = String(field || "");
              if (stringField.includes(",") || stringField.includes('"')) {
                return `"${stringField.replace(/"/g, '""')}"`;
              }
              return stringField;
            })
            .join(",");
        }),
      ];

      // Create CSV content
      const csvContent = csvRows.join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `transaction_report_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Success",
        description: "Transaction report exported successfully",
      });
    } catch (error) {
      console.error("Error exporting transactions:", error);
      toast({
        title: "Error",
        description: "Failed to export transaction report",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Mobile Number Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Mobile Number</label>
              <Input
                type="tel"
                inputMode="numeric"
                placeholder="Enter mobile number"
                value={mobileNoInput}
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/\D/g, "");
                  setMobileNoInput(numericValue);
                }}
                className="w-full"
              />
            </div>

            {/* From Date Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">From Date</label>
              <Popover
                open={filterFromDateOpen}
                onOpenChange={setFilterFromDateOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !filterFromDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filterFromDate ? (
                      format(filterFromDate, "PPP")
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filterFromDate}
                    onSelect={(date) => {
                      setFilterFromDate(date);
                      if (date) {
                        setFilterFromDateOpen(false);
                      }
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* To Date Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">To Date</label>
              <Popover
                open={filterToDateOpen}
                onOpenChange={setFilterToDateOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !filterToDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filterToDate ? (
                      format(filterToDate, "PPP")
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filterToDate}
                    onSelect={(date) => {
                      setFilterToDate(date);
                      if (date) {
                        setFilterToDateOpen(false);
                      }
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Transaction Type Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Transaction Type</label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={filterTxnType}
                onChange={(e) => setFilterTxnType(e.target.value)}
              >
                <option value="">All</option>
                <option value="Earn">Earn</option>
                <option value="Redeem">Redeem</option>
                <option value="NoEarnRedeem">No Earn/ Redeem</option>
              </select>
            </div>
          </div>

          {/* Reset Button */}
          <div className="flex justify-end mt-4">
            <Button
              variant="outline"
              onClick={handleResetFilters}
              className="flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Reset Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {isLoadingReports ? (
        <CouponTableSkeleton rowCount={5} />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-800">
              Transaction Report
            </h2>
            <Button
              onClick={handleExportTransactions}
              variant="outline"
              className="flex items-center gap-2"
              disabled={transactionReports.length === 0}
            >
              <Download className="h-4 w-4" />
              Export
            </Button>
          </div>
          <DataTable
            title=""
            data={transactionReports}
            cellClassName="py-6 text-gray-700"
            columns={[
              {
                header: "Date",
                id: "txnDate",
                cell: (row: TransactionReport) => formatTxnDateTime(row.txnDate),
              },
              {
                header: "Mobile Number",
                id: "mobileNo",
                cell: (row: TransactionReport) => displayReportText(row.mobileNo),
              },
              {
                header: "Invoice Number",
                id: "invoiceNo",
                cell: (row: TransactionReport) => displayReportText(row.invoiceNo),
              },
              {
                header: "Invoice Amount",
                id: "invoiceAmount",
                cell: (row: TransactionReport) =>
                  formatReportAmount(row.invoiceAmount),
              },
              {
                header: "Transaction Type",
                id: "transactionType",
                cell: (row: TransactionReport) =>
                  displayReportText(row.transactionType),
              },
              {
                header: "Coupon Code",
                id: "couponCode",
                cell: (row: TransactionReport) => displayReportText(row.couponCode),
              },
              {
                header: "Redeem Amount",
                id: "redeemAmount",
                cell: (row: TransactionReport) =>
                  formatReportAmount(row.redeemAmount),
              },
            ]}
          />

          {/* Pagination */}
          {!isLoadingReports && transactionTotalRecords > 0 && (
            <div className="mt-4 flex justify-between items-center">
              {/* Rows per page selector */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Rows per page:</span>
                <Select
                  value={transactionPageSize.toString()}
                  onValueChange={(value) => {
                    const newPageSize = parseInt(value, 10);
                    setTransactionPageSize(newPageSize);
                    setTransactionPage(0);
                  }}
                >
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="30">30</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Pagination controls */}
              {Math.ceil(transactionTotalRecords / transactionPageSize) > 1 && (
                <PaginationControls
                  currentPage={transactionPage}
                  totalPages={Math.ceil(
                    transactionTotalRecords / transactionPageSize
                  )}
                  onPrevHandler={() =>
                    setTransactionPage((p) => Math.max(0, p - 1))
                  }
                  onNextHandler={() =>
                    setTransactionPage((p) =>
                      Math.min(
                        Math.ceil(
                          transactionTotalRecords / transactionPageSize
                        ) - 1,
                        p + 1
                      )
                    )
                  }
                  onPageChange={(page) => setTransactionPage(page)}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
